import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Truck, Pencil, History } from "lucide-react";

const CATS = ["Fabric Supplier", "Printing Supplier", "Packaging Supplier", "Machine Supplier", "General Supplier"];

export default function Suppliers() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const canManage = can("suppliers.manage");
  const load = () => { setRows(null); api.get("/purchasing/suppliers").then((r) => setRows(r.data)); };
  useEffect(load, []);
  return (
    <div>
      <PageHeader title="Supplier" subtitle="Master supplier pembelian (kain, printing, packaging, dll)." testId="suppliers-header">
        {canManage && <Button onClick={() => setEdit({})} data-testid="add-supplier-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Supplier Baru</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada supplier" icon={Truck} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Kategori</TableHead><TableHead>Kontak</TableHead><TableHead>Kota</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-mono text-xs">{s.supplier_no}</TableCell>
                  <TableCell className="font-medium">{s.nama}</TableCell>
                  <TableCell className="text-slate-500">{s.kategori}</TableCell>
                  <TableCell className="text-xs text-slate-500">{s.contact_person || "-"}<br />{s.whatsapp || ""}</TableCell>
                  <TableCell className="text-slate-500">{s.kota || "-"}</TableCell>
                  <TableCell><StatusBadge status={s.status === "active" ? "Selesai" : "Dibatalkan"} label={s.status === "active" ? "Aktif" : "Nonaktif"} /></TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setHist(s)} data-testid={`supplier-hist-${s.id}`}><History className="h-4 w-4 text-slate-500" /></Button>
                    {canManage && <Button variant="ghost" size="sm" onClick={() => setEdit(s)} data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                  </div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {edit && <SupplierDialog supplier={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {hist && <HistoryDialog supplier={hist} onClose={() => setHist(null)} />}
    </div>
  );
}

function SupplierDialog({ supplier, onClose, onSaved }) {
  const isEdit = !!supplier;
  const [f, setF] = useState({
    nama: supplier?.nama || "", kategori: supplier?.kategori || CATS[0], contact_person: supplier?.contact_person || "",
    whatsapp: supplier?.whatsapp || "", email: supplier?.email || "", alamat: supplier?.alamat || "",
    kota: supplier?.kota || "", provinsi: supplier?.provinsi || "", npwp: supplier?.npwp || "",
    payment_terms: supplier?.payment_terms || "", notes: supplier?.notes || "", status: supplier?.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.nama) return toast.error("Nama supplier wajib diisi");
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/purchasing/suppliers/${supplier.id}`, f);
      else await api.post("/purchasing/suppliers", f);
      toast.success("Supplier disimpan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Ubah Supplier" : "Supplier Baru"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs text-slate-500">Nama Supplier</Label><Input className="mt-1" value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} data-testid="sup-nama" /></div>
          <div><Label className="text-xs text-slate-500">Kategori</Label><Select value={f.kategori} onValueChange={(v) => setF({ ...f, kategori: v })}><SelectTrigger className="mt-1" data-testid="sup-kategori"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Contact Person</Label><Input className="mt-1" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">No. WhatsApp</Label><Input className="mt-1" value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="sup-wa" /></div>
          <div><Label className="text-xs text-slate-500">Email</Label><Input className="mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Kota</Label><Input className="mt-1" value={f.kota} onChange={(e) => setF({ ...f, kota: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Provinsi</Label><Input className="mt-1" value={f.provinsi} onChange={(e) => setF({ ...f, provinsi: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">NPWP</Label><Input className="mt-1" value={f.npwp} onChange={(e) => setF({ ...f, npwp: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Payment Terms</Label><Input className="mt-1" value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} placeholder="mis. NET 30" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Alamat</Label><Textarea className="mt-1" rows={2} value={f.alamat} onChange={(e) => setF({ ...f, alamat: e.target.value })} /></div>
          {isEdit && <div><Label className="text-xs text-slate-500">Status</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent></Select></div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-supplier-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ supplier, onClose }) {
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/purchasing/suppliers/${supplier.id}/history`).then((r) => setH(r.data)); }, [supplier.id]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="supplier-history-dialog">
        <DialogHeader><DialogTitle className="font-display">Riwayat Pembelian — {supplier.nama}</DialogTitle></DialogHeader>
        {!h ? <Loading /> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Jumlah PO" value={numberID(h.po_count)} />
              <Stat label="Total Nilai Pembelian" value={rupiah(h.total_value)} />
              <Stat label="Pembelian Terakhir" value={h.last_purchase_date || "-"} />
              <Stat label="Item Terakhir" value={h.last_item || "-"} />
              <Stat label="Harga Terakhir" value={h.last_price ? rupiah(h.last_price) : "-"} />
              <Stat label="Rata-rata Lead Time" value={h.avg_lead_time_days != null ? `${h.avg_lead_time_days} hari` : "Data belum cukup"} />
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const Stat = ({ label, value }) => (<div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-[11px] text-slate-400">{label}</p><p className="font-semibold text-slate-800">{value}</p></div>);
